import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { CarnivoreAlert } from '../_types/alert.type'
import { useError } from './error'
import { AlertService } from '../services/alert.service'
import { useGlobalState } from './global'

interface LiveAlertContextType {
  liveAlert: CarnivoreAlert | null
  setLiveAlert: (alert: CarnivoreAlert | null) => void
}

const LiveAlertContext = createContext<LiveAlertContextType>({
  liveAlert: null,
  setLiveAlert: () => { }
})

export const useLiveAlert = () => useContext(LiveAlertContext)

const POLL_MS = 1000 * 45; // adjust as needed

export const LiveAlertProvider = ({ children }: { children: ReactNode }) => {
  const [liveAlert, setLiveAlert] = useState<CarnivoreAlert | null>(null)
  const lastAlertIdRef = useRef<string | null>(null)

  const { user } = useGlobalState()

  const alertService = new AlertService(() => { })

  const flashIntervalRef = useRef<number | null>(null)
  const originalTitleRef = useRef<string | null>(null)
  const originalFaviconHrefRef = useRef<string | null>(null)

  function getFaviconEl(): HTMLLinkElement | null {
    return document.querySelector<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]')
  }

  function getCurrentFaviconHref(): string | null {
    const el = getFaviconEl()
    return el?.href ?? null
  }

  function setFaviconHref(href: string) {
    let el = getFaviconEl()
    if (!el) {
      el = document.createElement('link')
      el.rel = 'icon'
      document.head.appendChild(el)
    }
    el.href = href
  }

  function makeRedDotFavicon(size = 32): string {
    const c = document.createElement('canvas')
    c.width = size
    c.height = size
    const ctx = c.getContext('2d')!
    // background transparent
    // red circle
    const r = size * 0.35
    ctx.beginPath()
    ctx.arc(size / 2, size / 2, r, 0, Math.PI * 2)
    ctx.fillStyle = '#ff2d2d'
    ctx.fill()
    // subtle white highlight
    ctx.beginPath()
    ctx.arc(size / 2 - r * 0.35, size / 2 - r * 0.35, r * 0.25, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(255,255,255,0.6)'
    ctx.fill()
    return c.toDataURL('image/png')
  }

  // ===== FLASH CONTROL =====
  function stopAttention() {
    if (flashIntervalRef.current) {
      window.clearInterval(flashIntervalRef.current)
      flashIntervalRef.current = null
    }
    if (originalTitleRef.current !== null) {
      document.title = originalTitleRef.current
    }
    if (originalFaviconHrefRef.current) {
      setFaviconHref(originalFaviconHrefRef.current)
    }
  }

  function startAttention(alertTitle: string) {
    // Save originals once per cycle
    if (originalTitleRef.current === null) {
      originalTitleRef.current = document.title
    }
    if (!originalFaviconHrefRef.current) {
      originalFaviconHrefRef.current = getCurrentFaviconHref()
    }

    const redDot = makeRedDotFavicon(32)
    const flashText = `New Alert | Carnivore Trading`
    let showFlash = true

    document.title = flashText
    setFaviconHref(redDot)

    flashIntervalRef.current = window.setInterval(() => {
      // Toggle title
      document.title = showFlash ? originalTitleRef.current || document.title : flashText
      // Toggle favicon
      setFaviconHref(showFlash ? originalFaviconHrefRef.current! : redDot)
      showFlash = !showFlash
    }, 1500)

    // Stop after 5 seconds
    window.setTimeout(() => {
      stopAttention()
    }, 5000)
  }

  // single tick
  const tick = async () => {
    if (!user?.id) return
    if (typeof document !== 'undefined' && document.hidden) return

    try {
      const alert = await alertService.getNewestAlert()
      if (alert?.id && alert.id !== lastAlertIdRef.current && lastAlertIdRef.current) {
        lastAlertIdRef.current = alert.id
        setLiveAlert(alert)
        stopAttention()
        startAttention(alert.title ?? 'Alert')
      } else {
        lastAlertIdRef.current = alert.id
      }
    } catch (err: any) {
      console.log(err)
    }
  }

  // start/stop polling when user changes
  useEffect(() => {
    // reset state when user flips
    lastAlertIdRef.current = null
    setLiveAlert(null)

    if (!user?.id) return

    let intervalId: number | undefined

    // fire immediately, then poll
    tick()
    intervalId = window.setInterval(tick, POLL_MS)

    return () => {
      if (intervalId) window.clearInterval(intervalId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  return (
    <LiveAlertContext.Provider value={{ liveAlert, setLiveAlert }}>
      {children}
    </LiveAlertContext.Provider>
  )
}
